import React from "react";

import { motion, AnimatePresence } from "framer-motion";

const SubtitlesModal = ({
  isOpen,
  onClose,
  subtitles,
  selectedSubtitle,
  onSelectSubtitle,
  nativeSubtitlesEnabled,
  onToggleNativeSubtitles,
  loading,
}) => {
  const getFlagForLanguage = (language) => {
    switch (language?.toLowerCase()) {
      case "english":
      case "en":
        return "\u{1F1EC}\u{1F1E7}"; // GB flag
      case "spanish":
      case "es":
        return "\u{1F1EA}\u{1F1F8}"; // ES flag
      case "arabic":
      case "ar":
        return "\u{1F1F8}\u{1F1E6}"; // SA flag
      case "portuguese":
      case "pt":
        return "\u{1F1F5}\u{1F1F9}"; // PT flag
      case "french":
      case "fr":
        return "\u{1F1EB}\u{1F1F7}"; // FR flag
      case "german":
      case "de":
        return "\u{1F1E9}\u{1F1EA}"; // DE flag
      case "italian":
      case "it":
        return "\u{1F1EE}\u{1F1F9}"; // IT flag
      case "russian":
      case "ru":
        return "\u{1F1F7}\u{1F1FA}"; // RU flag
      case "chinese":
      case "zh":
        return "\u{1F1E8}\u{1F1F3}"; // CN flag
      case "japanese":
      case "ja":
        return "\u{1F1EF}\u{1F1F5}"; // JP flag
      case "korean":
      case "ko":
        return "\u{1F1F0}\u{1F1F7}"; // KR flag
      case "dutch":
      case "nl":
        return "\u{1F1F3}\u{1F1F1}"; // NL flag
      case "polish":
      case "pl":
        return "\u{1F1F5}\u{1F1F1}"; // PL flag
      case "turkish":
      case "tr":
        return "\u{1F1F9}\u{1F1F7}"; // TR flag
      case "hindi":
      case "hi":
        return "\u{1F1EE}\u{1F1F3}"; // IN flag
      case "hebrew":
      case "he":
        return "\u{1F1EE}\u{1F1F1}"; // IL flag
      default:
        return "\u{1F310}"; // Globe icon fallback
    }
  };

  const formatDownloads = (downloads) => {
    if (downloads >= 1000) {
      return `${(downloads / 1000).toFixed(1)}k`;
    }

    return downloads.toString();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[100001] flex items-center justify-center bg-black/70 backdrop-blur-sm"
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          animate={{ scale: 1, opacity: 1 }}
          className="relative w-full max-w-xl mx-4 bg-gray-900/95 rounded-2xl overflow-hidden shadow-2xl border border-gray-700/50"
          exit={{ scale: 0.95, opacity: 0 }}
          initial={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-700/50">
            <h2 className="text-2xl font-bold text-white">Subtitles</h2>
          </div>

          {/* Content */}
          <div className="max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-purple-500" />
              </div>
            ) : (
              <div className="divide-y divide-gray-700/30">
                {/* Native Subtitles Option - Always visible with toggle */}
                <button
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-800/50 transition-colors"
                  onClick={() => onToggleNativeSubtitles()}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{"\u{1F3AC}"}</span>
                    <span className="text-white font-medium">
                      Native Subtitles
                    </span>
                  </div>
                  {/* Toggle Switch */}
                  <div
                    className={`relative w-12 h-7 rounded-full transition-colors duration-200 ${
                      nativeSubtitlesEnabled ? "bg-purple-600" : "bg-gray-600"
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 ${
                        nativeSubtitlesEnabled
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </div>
                </button>

                {/* External Subtitles List */}
                {subtitles && subtitles.length > 0
                  ? subtitles.map((subtitle, index) => {
                      const isSelected =
                        selectedSubtitle?.url === subtitle.url &&
                        !nativeSubtitlesEnabled;

                      return (
                        <button
                          className={`w-full px-6 py-4 flex items-center justify-between hover:bg-gray-800/50 transition-colors ${
                            isSelected ? "bg-gray-800/30" : ""
                          } ${index % 2 === 0 ? "bg-gray-800/10" : ""}`}
                          key={subtitle.url || index}
                          onClick={() => onSelectSubtitle(subtitle)}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="text-xl flex-shrink-0">
                              {getFlagForLanguage(subtitle.language)}
                            </span>
                            <span className="text-white truncate">
                              <span className="text-gray-400">
                                [{subtitle.language}]
                              </span>{" "}
                              {subtitle.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                            <span className="text-gray-400 text-sm">
                              {formatDownloads(subtitle.downloads)}
                            </span>
                            <span
                              className={`text-xl ${isSelected ? "text-blue-400" : "text-gray-500"}`}
                            >
                              {isSelected ? (
                                <i className="fas fa-check-circle" />
                              ) : (
                                <i className="fas fa-arrow-circle-down" />
                              )}
                            </span>
                          </div>
                        </button>
                      );
                    })
                  : null}
              </div>
            )}
          </div>

          {/* Footer with disable option if subtitle is selected */}
          {(selectedSubtitle || nativeSubtitlesEnabled) && (
            <div className="px-6 py-4 border-t border-gray-700/50">
              <button
                className="w-full py-3 px-4 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg font-medium transition-colors"
                onClick={() => {
                  if (nativeSubtitlesEnabled) {
                    onToggleNativeSubtitles();
                  } else {
                    onSelectSubtitle(null);
                  }
                }}
              >
                <i className="fas fa-times-circle mr-2" />
                Disable Subtitles
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SubtitlesModal;
